import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";

//MUI
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";

import FormikControl from "../../components/formik/FormikControl";
import Loader from "../../components/loader/Loader";

import BUService from "../../services/BUService";
import { Mode } from "./BUPage";
import { BUModel } from "../../models/BUModel";
// import { statusOptions } from "../../utility/utils";
import { fetchBUs } from "../../redux/actions/BUActions";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { createAutocompleteOptions } from "../../utility/utils";

const validationSchema = Yup.object({
  name: Yup.string().required("Required"),
  description: Yup.string().required("Required"),
  isApprovalRequired: Yup.string().required("Required"),
  buuserId: Yup.number().notRequired(),
  buviewers: Yup.array().notRequired(),
});

type FormProps = Yup.InferType<typeof validationSchema>;

type Props = {
  mode: Mode;
  handleModalClose: () => void;
  data?: BUModel;
};

const BUForm = ({ mode, handleModalClose, data }: Props) => {
  const initialValues = useMemo(
    () => ({
      name: "",
      description: "",
      isApprovalRequired: "",
      buuserId: 0,
      buviewers: [],
    }),
    []
  );

  const [formState, setFormState] = useState<FormProps>(initialValues);
  const dispatch = useAppDispatch();
  const displayToast = useAppSelector((state) => state.toast.displayToast);
  const allUsers = useAppSelector((state) =>
    state.user.all_Users.filter((rec) => rec.roleId === 3)
  );

  const approvalOptions = [
    {
      value: "required",
      label: "Yes",
    },
    {
      value: "notRequired",
      label: "No",
    },
  ];

  const userOptions = useMemo(() => {
    return createAutocompleteOptions(
      allUsers,
      "userId",
      "firstName",
      "lastName"
    );
  }, [allUsers]);

  useEffect(() => {
    if (mode === "edit" && data) {
      setFormState(() => ({
        name: data.name,
        description: data.description,
        isApprovalRequired:
          data.isApprovalRequired !== null
            ? data.isApprovalRequired
              ? "required"
              : "notRequired"
            : "notRequired",
        buuserId: data.buuserId,
        buviewers: data.buviewers
          ? data.buviewers.split(",").map((val) => +val)
          : [],
      }));
    }
  }, [mode, data]);

  const submitHandler = async (
    values: FormProps,
    actions: FormikHelpers<FormProps>
  ) => {
    try {
      let formData: BUModel = {
        buid: 0,
        name: "",
        buuserId: 0,
        buviewers: "",
        isApprovalRequired: false,
        description: "",
        isActive: true,
        createdDate: new Date(),
        modifiedDate: new Date(),
      };
      if (mode === "add") {
        formData.name = values.name;
        formData.description = values.description;
        formData.isApprovalRequired =
          values.isApprovalRequired === "required" ? true : false;
        formData.buuserId = values.buuserId ? values.buuserId : 0;
        formData.buviewers = values.buviewers?.length
          ? values.buviewers?.join(",")
          : "";
      } else if (data) {
        formData = {
          ...data,
          name: values.name,
          description: values.description,
          isApprovalRequired:
            values.isApprovalRequired === "required" ? true : false,
          buuserId: values.buuserId ? values.buuserId : 0,
          buviewers: values.buviewers?.length
            ? values.buviewers?.join(",")
            : "",
          modifiedDate: new Date(),
        };
      }
      await BUService.createBU(formData, data?.buid);
      actions.setSubmitting(false);
      handleModalClose();
      dispatch(fetchBUs());
      displayToast(
        `BU ${mode === "add" ? "created" : "updated"} successfully`,
        "success"
      );
    } catch (error) {
      actions.setSubmitting(false);
    }
  };
  return (
    <Formik
      initialValues={formState}
      validationSchema={validationSchema}
      onSubmit={submitHandler}
      enableReinitialize={true}
    >
      {(formik) => {
        return (
          <>
            <Form>
              <Stack gap={2}>
                <FormikControl
                  control="input"
                  type="text"
                  name="name"
                  size="small"
                  label="Name"
                  placeholder="Enter Name"
                />
                <FormikControl
                  control="input"
                  multiline
                  maxRows={2}
                  name="description"
                  size="small"
                  label="Description"
                  placeholder="Enter Description"
                />
                <FormikControl
                  control="autocomplete"
                  options={approvalOptions}
                  name="isApprovalRequired"
                  size="small"
                  label="Is Approval Required"
                  placeholder="Select isApprovalRequired"
                />
                {formik.values.isApprovalRequired === "required" ||
                data?.buuserId ? (
                  <FormikControl
                    control="autocomplete"
                    options={userOptions}
                    disabled={formik.values.isApprovalRequired !== "required"}
                    name="buuserId"
                    size="small"
                    label="Select BU user"
                    placeholder="Select BU user"
                  />
                ) : null}
                <FormikControl
                  control="autocomplete"
                  options={userOptions}
                  name="buviewers"
                  multiple={true}
                  size="small"
                  label="Select BU Viewers"
                  placeholder="Select BU Viewers"
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting}
                >
                  Submit
                </Button>
              </Stack>
            </Form>
            {formik.isSubmitting && <Loader />}
          </>
        );
      }}
    </Formik>
  );
};

export default BUForm;
